<template>
  <div class="home-layout">
    <div class="home-layout-toolbar">
      <TrackDifficultyFilter v-if="viewMode === 'dashboard'" v-model="difficultyFilter" />
      <div class="home-layout-toolbar-actions">
        <TracksViewToggle v-model="viewMode" />
        <TrackAddMenu
          v-if="auth.isAuthenticated && viewMode === 'map'"
          @uploaded="onTrackUploaded"
        />
      </div>
    </div>

    <TracksMapView v-if="viewMode === 'map'" />

    <template v-else>
    <div v-if="auth.isAuthenticated" class="home-dashboard-mobile">
      <div class="dashboard-tab-bar" role="tablist" aria-label="Разделы треков">
        <button
          v-for="tab in dashboardTabs"
          :key="tab.id"
          type="button"
          class="dashboard-tab"
          :class="{ 'dashboard-tab--active': activeTab === tab.id }"
          role="tab"
          :aria-selected="activeTab === tab.id"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>

      <section class="home-dashboard-panel panel">
        <div v-if="activeTab === 'personal'" class="home-dashboard-tab">
          <div class="column-header column-header--tabs">
            <div class="column-header-tabs-row">
              <div class="dashboard-tab-bar" role="tablist" aria-label="Мои и избранное">
                <button
                  v-for="tab in personalSubTabs"
                  :key="tab.id"
                  type="button"
                  class="dashboard-tab"
                  :class="{ 'dashboard-tab--active': personalSubTab === tab.id }"
                  role="tab"
                  :aria-selected="personalSubTab === tab.id"
                  @click="personalSubTab = tab.id"
                >
                  {{ tab.label }}
                </button>
              </div>
              <TrackAddMenu v-if="personalSubTab === 'my'" @uploaded="onTrackUploaded" />
            </div>
          </div>
          <div v-if="personalSubTab === 'my'" class="column-body">
            <p v-if="myLoading" class="empty-state">Загрузка...</p>
            <TrackList
              v-else
              title=""
              :tracks="myTracks"
              :empty-text="myTracksEmptyText"
              @select="openTrack"
            />
            <LoadMoreButton
              :has-more="hasMorePages(myPagination)"
              :loading="myLoadingMore"
              :disabled="myLoading"
              @click="loadMoreMyTracks"
            />
          </div>
          <div v-if="personalSubTab === 'favorites'" class="column-body">
            <p v-if="favoriteTracksLoading" class="empty-state">Загрузка...</p>
            <TrackList
              v-else
              title="Треки"
              :tracks="favoriteTracks"
              :empty-text="favoriteTracksEmptyText"
              @select="openTrack"
            />
            <LoadMoreButton
              :has-more="hasMorePages(favoriteTracksPagination)"
              :loading="favoriteTracksLoadingMore"
              :disabled="favoriteTracksLoading"
              @click="loadMoreFavoriteTracks"
            />
            <AuthorFavoriteList
              title="Авторы"
              :authors="favoriteAuthors"
              :removing-id="removingAuthorId"
              @remove="removeFavoriteAuthor"
            />
            <LoadMoreButton
              :has-more="hasMorePages(favoriteAuthorsPagination)"
              :loading="favoriteAuthorsLoadingMore"
              :disabled="favoriteAuthorsLoading"
              @click="loadMoreFavoriteAuthors"
            />
          </div>
        </div>

        <div v-else-if="activeTab === 'public'" class="home-dashboard-tab">
          <div class="column-header">
            <ColumnFilter
              v-model="publicTracksSort"
              :options="publicTracksFilterOptions"
              :loading="publicTracksLoading"
              @update:model-value="onPublicSortChange"
            />
          </div>
          <div class="column-body">
            <p v-if="publicTracksLoading" class="empty-state">Загрузка...</p>
            <TrackList
              v-else
              title=""
              :tracks="publicTracks"
              :empty-text="publicTracksEmptyText"
              @select="openTrack"
            />
            <LoadMoreButton
              :has-more="hasMorePages(publicPagination)"
              :loading="publicTracksLoadingMore"
              :disabled="publicTracksLoading"
              @click="loadMorePublicTracks"
            />
          </div>
        </div>

        <div v-else class="home-dashboard-tab">
          <h2>Последние комментарии</h2>
          <div class="column-body">
            <RecentCommentFeed
              kind="track"
              :comments="recentComments"
              :loading="recentCommentsLoading"
            />
            <LoadMoreButton
              :has-more="hasMorePages(recentCommentsPagination)"
              :loading="recentCommentsLoadingMore"
              :disabled="recentCommentsLoading"
              @click="loadMoreRecentComments"
            />
          </div>
        </div>
      </section>
    </div>

    <div v-if="auth.isAuthenticated" class="home-columns home-columns--desktop">
      <section class="dashboard-column panel">
        <div class="column-header column-header--tabs">
          <div class="column-header-tabs-row">
            <div class="dashboard-tab-bar" role="tablist" aria-label="Мои и избранное">
              <button
                v-for="tab in personalSubTabs"
                :key="tab.id"
                type="button"
                class="dashboard-tab"
                :class="{ 'dashboard-tab--active': personalSubTab === tab.id }"
                role="tab"
                :aria-selected="personalSubTab === tab.id"
                @click="personalSubTab = tab.id"
              >
                {{ tab.label }}
              </button>
            </div>
            <TrackAddMenu v-if="personalSubTab === 'my'" @uploaded="onTrackUploaded" />
          </div>
        </div>
        <div v-if="personalSubTab === 'my'" class="column-body">
          <TrackList
            title=""
            :tracks="myTracks"
            :empty-text="myTracksEmptyText"
            @select="openTrack"
          />
          <LoadMoreButton
            :has-more="hasMorePages(myPagination)"
            :loading="myLoadingMore"
            :disabled="myLoading"
            @click="loadMoreMyTracks"
          />
        </div>
        <div v-else class="column-body">
          <TrackList
            title="Треки"
            :tracks="favoriteTracks"
            :empty-text="favoriteTracksEmptyText"
            @select="openTrack"
          />
          <LoadMoreButton
            :has-more="hasMorePages(favoriteTracksPagination)"
            :loading="favoriteTracksLoadingMore"
            :disabled="favoriteTracksLoading"
            @click="loadMoreFavoriteTracks"
          />
          <AuthorFavoriteList
            title="Авторы"
            :authors="favoriteAuthors"
            :removing-id="removingAuthorId"
            @remove="removeFavoriteAuthor"
          />
          <LoadMoreButton
            :has-more="hasMorePages(favoriteAuthorsPagination)"
            :loading="favoriteAuthorsLoadingMore"
            :disabled="favoriteAuthorsLoading"
            @click="loadMoreFavoriteAuthors"
          />
        </div>
      </section>

      <section class="dashboard-column panel">
        <div class="column-header">
          <ColumnFilter
            v-model="publicTracksSort"
            :options="publicTracksFilterOptions"
            :loading="publicTracksLoading"
            @update:model-value="onPublicSortChange"
          />
        </div>
        <div class="column-body">
          <p v-if="publicTracksLoading" class="empty-state">Загрузка...</p>
          <TrackList
            v-else
            title=""
            :tracks="publicTracks"
            :empty-text="publicTracksEmptyText"
            @select="openTrack"
          />
          <LoadMoreButton
            :has-more="hasMorePages(publicPagination)"
            :loading="publicTracksLoadingMore"
            :disabled="publicTracksLoading"
            @click="loadMorePublicTracks"
          />
        </div>
      </section>

      <section class="dashboard-column panel">
        <h2>Последние комментарии</h2>
        <div class="column-body">
          <RecentCommentFeed
            kind="track"
            :comments="recentComments"
            :loading="recentCommentsLoading"
          />
          <LoadMoreButton
            :has-more="hasMorePages(recentCommentsPagination)"
            :loading="recentCommentsLoadingMore"
            :disabled="recentCommentsLoading"
            @click="loadMoreRecentComments"
          />
        </div>
      </section>
    </div>

    <div v-else class="guest-home">
      <div class="guest-home-mobile home-dashboard-mobile">
        <div class="dashboard-tab-bar" role="tablist" aria-label="Разделы треков">
          <button
            v-for="tab in guestTabs"
            :key="tab.id"
            type="button"
            class="dashboard-tab"
            :class="{ 'dashboard-tab--active': guestActiveTab === tab.id }"
            role="tab"
            :aria-selected="guestActiveTab === tab.id"
            @click="guestActiveTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </div>

        <section class="home-dashboard-panel panel">
          <div v-if="guestActiveTab === 'public'" class="home-dashboard-tab">
            <div class="column-header">
              <h1>Публичные треки</h1>
              <ColumnFilter
                v-model="publicTracksSort"
                :options="publicTracksFilterOptions"
                :loading="publicTracksLoading"
                @update:model-value="onPublicSortChange"
              />
            </div>
            <div class="column-body">
              <p v-if="publicTracksLoading" class="empty-state">Загрузка...</p>
              <TrackList
                v-else
                title=""
                :tracks="publicTracks"
                :empty-text="publicTracksEmptyText"
                @select="openTrack"
              />
              <LoadMoreButton
                :has-more="hasMorePages(publicPagination)"
                :loading="publicTracksLoadingMore"
                :disabled="publicTracksLoading"
                @click="loadMorePublicTracks"
              />
            </div>
          </div>

          <div v-else class="home-dashboard-tab">
            <h2 class="guest-column-title">Последние комментарии</h2>
            <div class="column-body">
              <RecentCommentFeed
                kind="track"
                :comments="recentComments"
                :loading="recentCommentsLoading"
              />
              <LoadMoreButton
                :has-more="hasMorePages(recentCommentsPagination)"
                :loading="recentCommentsLoadingMore"
                :disabled="recentCommentsLoading"
                @click="loadMoreRecentComments"
              />
            </div>
          </div>
        </section>
      </div>

      <div class="guest-home-columns guest-home-columns--desktop">
        <section class="panel guest-home-column">
          <div class="column-header">
            <h1>Публичные треки</h1>
            <ColumnFilter
              v-model="publicTracksSort"
              :options="publicTracksFilterOptions"
              :loading="publicTracksLoading"
              @update:model-value="onPublicSortChange"
            />
          </div>
          <div class="column-body">
            <p v-if="publicTracksLoading" class="empty-state">Загрузка...</p>
            <TrackList
              v-else
              title=""
              :tracks="publicTracks"
              :empty-text="publicTracksEmptyText"
              @select="openTrack"
            />
            <LoadMoreButton
              :has-more="hasMorePages(publicPagination)"
              :loading="publicTracksLoadingMore"
              :disabled="publicTracksLoading"
              @click="loadMorePublicTracks"
            />
          </div>
        </section>

        <section class="panel guest-home-column">
          <h2 class="guest-column-title">Последние комментарии</h2>
          <div class="column-body">
            <RecentCommentFeed
              kind="track"
              :comments="recentComments"
              :loading="recentCommentsLoading"
            />
            <LoadMoreButton
              :has-more="hasMorePages(recentCommentsPagination)"
              :loading="recentCommentsLoadingMore"
              :disabled="recentCommentsLoading"
              @click="loadMoreRecentComments"
            />
          </div>
        </section>
      </div>
    </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AuthorFavoriteList from '../components/AuthorFavoriteList.vue'
import ColumnFilter from '../components/ColumnFilter.vue'
import LoadMoreButton from '../components/LoadMoreButton.vue'
import RecentCommentFeed from '../components/RecentCommentFeed.vue'
import TrackAddMenu from '../components/TrackAddMenu.vue'
import TrackDifficultyFilter from '../components/TrackDifficultyFilter.vue'
import TrackList from '../components/TrackList.vue'
import TracksMapView from '../components/TracksMapView.vue'
import TracksViewToggle from '../components/TracksViewToggle.vue'
import { useMediaQuery } from '../composables/useMediaQuery'
import { fetchFavoriteTracks, fetchFavoriteUsers, unfavoriteUser } from '../api/favorites'
import { fetchPublicTrackComments } from '../api/comments'
import { fetchPublicTracks, fetchTracks } from '../api/tracks'
import { useAuthStore } from '../stores/auth'
import { hasMorePages, parsePaginatedList } from '../utils/pagination'

const PAGE_SIZE = 10
const VIEW_MODE_STORAGE_KEY = 'tracksViewMode'

const router = useRouter()
const auth = useAuthStore()
const isMobile = useMediaQuery('(max-width: 767px)')

function initialViewMode() {
  const value = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY)
  return value === 'map' ? 'map' : 'dashboard'
}

const myTracks = ref([])
const publicTracks = ref([])
const favoriteTracks = ref([])
const favoriteAuthors = ref([])
const removingAuthorId = ref(null)
const publicTracksSort = ref('new')
const publicTracksLoading = ref(false)
const publicTracksLoadingMore = ref(false)
const myLoading = ref(false)
const myLoadingMore = ref(false)
const favoriteTracksLoading = ref(false)
const favoriteTracksLoadingMore = ref(false)
const favoriteAuthorsLoading = ref(false)
const favoriteAuthorsLoadingMore = ref(false)
const recentComments = ref([])
const recentCommentsLoading = ref(false)
const recentCommentsLoadingMore = ref(false)
const difficultyFilter = ref({ season: 'summer', wheelSize: '' })
const viewMode = ref(initialViewMode())

const activeTab = ref('personal')
const personalSubTab = ref('my')
const myPage = ref(1)
const publicPage = ref(1)
const favoriteTracksPage = ref(1)
const favoriteAuthorsPage = ref(1)
const recentCommentsPage = ref(1)
const myPagination = ref(null)
const publicPagination = ref(null)
const favoriteTracksPagination = ref(null)
const favoriteAuthorsPagination = ref(null)
const recentCommentsPagination = ref(null)

const dashboardTabs = [
  { id: 'personal', label: 'Личное' },
  { id: 'public', label: 'На сайте' },
  { id: 'comments', label: 'Комментарии' },
]

const personalSubTabs = [
  { id: 'my', label: 'Мои треки' },
  { id: 'favorites', label: 'Избранное' },
]

const guestActiveTab = ref('public')

const guestTabs = [
  { id: 'public', label: 'На сайте' },
  { id: 'comments', label: 'Комментарии' },
]

const publicTracksFilterOptions = [
  { value: 'new', label: 'Новые на сайте' },
  { value: 'most_commented', label: 'Самые обсуждаемые' },
  { value: 'most_liked', label: 'Самые популярные' },
  { value: 'most_completed', label: 'Самые изъезженные' },
]

const isDifficultyFilterActive = computed(() => Boolean(difficultyFilter.value.wheelSize))

function difficultyParams() {
  return {
    season: difficultyFilter.value.season,
    wheelSize: difficultyFilter.value.wheelSize || undefined,
  }
}

const myTracksEmptyText = computed(() => {
  if (isDifficultyFilterActive.value && myTracks.value.length === 0 && !myLoading.value) {
    return 'Нет треков с выбранной сложностью.'
  }
  return 'Пока нет сохранённых треков.'
})

const publicTracksEmptyText = computed(() => {
  if (isDifficultyFilterActive.value && publicTracks.value.length === 0 && !publicTracksLoading.value) {
    return 'Нет треков с выбранной сложностью.'
  }
  return 'Пока нет публичных треков.'
})

const favoriteTracksEmptyText = computed(() => {
  if (isDifficultyFilterActive.value && favoriteTracks.value.length === 0 && !favoriteTracksLoading.value) {
    return 'Нет треков с выбранной сложностью.'
  }
  return 'Пока нет избранных треков.'
})

async function loadMyTracks({ reset = true } = {}) {
  if (reset) {
    myLoading.value = true
    myPage.value = 1
  } else {
    myLoadingMore.value = true
  }
  try {
    const response = await fetchTracks({
      page: myPage.value,
      perPage: PAGE_SIZE,
      ...difficultyParams(),
    })
    const { items, pagination } = parsePaginatedList(response, 'tracks')
    myTracks.value = reset ? items : [...myTracks.value, ...items]
    myPagination.value = pagination
  } finally {
    myLoading.value = false
    myLoadingMore.value = false
  }
}

function loadMoreMyTracks() {
  myPage.value += 1
  loadMyTracks({ reset: false })
}

async function loadPublicTracks({ reset = true } = {}) {
  if (reset) {
    publicTracksLoading.value = true
    publicPage.value = 1
  } else {
    publicTracksLoadingMore.value = true
  }
  try {
    const response = await fetchPublicTracks({
      sort: publicTracksSort.value,
      page: publicPage.value,
      perPage: PAGE_SIZE,
      ...difficultyParams(),
    })
    const { items, pagination } = parsePaginatedList(response, 'tracks')
    publicTracks.value = reset ? items : [...publicTracks.value, ...items]
    publicPagination.value = pagination
  } finally {
    publicTracksLoading.value = false
    publicTracksLoadingMore.value = false
  }
}

function loadMorePublicTracks() {
  publicPage.value += 1
  loadPublicTracks({ reset: false })
}

async function loadFavoriteTracks({ reset = true } = {}) {
  if (reset) {
    favoriteTracksLoading.value = true
    favoriteTracksPage.value = 1
  } else {
    favoriteTracksLoadingMore.value = true
  }
  try {
    const response = await fetchFavoriteTracks({
      page: favoriteTracksPage.value,
      perPage: PAGE_SIZE,
      ...difficultyParams(),
    })
    const { items, pagination } = parsePaginatedList(response, 'tracks')
    favoriteTracks.value = reset ? items : [...favoriteTracks.value, ...items]
    favoriteTracksPagination.value = pagination
  } finally {
    favoriteTracksLoading.value = false
    favoriteTracksLoadingMore.value = false
  }
}

function loadMoreFavoriteTracks() {
  favoriteTracksPage.value += 1
  loadFavoriteTracks({ reset: false })
}

async function loadFavoriteAuthors({ reset = true } = {}) {
  if (reset) {
    favoriteAuthorsLoading.value = true
    favoriteAuthorsPage.value = 1
  } else {
    favoriteAuthorsLoadingMore.value = true
  }
  try {
    const response = await fetchFavoriteUsers({
      page: favoriteAuthorsPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'authors')
    favoriteAuthors.value = reset ? items : [...favoriteAuthors.value, ...items]
    favoriteAuthorsPagination.value = pagination
  } finally {
    favoriteAuthorsLoading.value = false
    favoriteAuthorsLoadingMore.value = false
  }
}

function loadMoreFavoriteAuthors() {
  favoriteAuthorsPage.value += 1
  loadFavoriteAuthors({ reset: false })
}

async function loadFavorites({ reset = true } = {}) {
  await Promise.all([loadFavoriteTracks({ reset }), loadFavoriteAuthors({ reset })])
}

async function loadRecentComments({ reset = true } = {}) {
  if (reset) {
    recentCommentsLoading.value = true
    recentCommentsPage.value = 1
  } else {
    recentCommentsLoadingMore.value = true
  }
  try {
    const response = await fetchPublicTrackComments({
      page: recentCommentsPage.value,
      perPage: PAGE_SIZE,
    })
    const { items, pagination } = parsePaginatedList(response, 'comments')
    recentComments.value = reset ? items : [...recentComments.value, ...items]
    recentCommentsPagination.value = pagination
  } finally {
    recentCommentsLoading.value = false
    recentCommentsLoadingMore.value = false
  }
}

function loadMoreRecentComments() {
  recentCommentsPage.value += 1
  loadRecentComments({ reset: false })
}

async function loadDesktopTracks() {
  await Promise.all([
    loadMyTracks({ reset: true }),
    loadFavoriteTracks({ reset: true }),
    loadFavoriteAuthors({ reset: true }),
    loadPublicTracks({ reset: true }),
    loadRecentComments({ reset: true }),
  ])
}

async function loadPersonalMobile() {
  if (personalSubTab.value === 'my') {
    await loadMyTracks({ reset: true })
  } else {
    await loadFavorites({ reset: true })
  }
}

async function loadMobileTab(tab = activeTab.value) {
  if (tab === 'personal') {
    await loadPersonalMobile()
  } else if (tab === 'public') {
    await loadPublicTracks({ reset: true })
  } else {
    await loadRecentComments({ reset: true })
  }
}

async function loadGuestMobileTab(tab = guestActiveTab.value) {
  if (tab === 'public') {
    await loadPublicTracks({ reset: true })
  } else {
    await loadRecentComments({ reset: true })
  }
}

async function loadGuestTracks() {
  await Promise.all([loadPublicTracks({ reset: true }), loadRecentComments({ reset: true })])
}

async function loadTracks() {
  if (!auth.isAuthenticated) {
    if (isMobile.value) {
      await loadGuestMobileTab()
    } else {
      await loadGuestTracks()
    }
    return
  }

  if (isMobile.value) {
    await loadMobileTab()
  } else {
    await loadDesktopTracks()
  }
}

function onPublicSortChange() {
  loadPublicTracks({ reset: true })
}

function onTrackUploaded(track) {
  if (!auth.isAuthenticated) return

  if (isMobile.value) {
    activeTab.value = 'personal'
    personalSubTab.value = 'my'
  }

  if (viewMode.value === 'map') {
    viewMode.value = 'dashboard'
  }

  if (myTracks.value.some((item) => item.id === track.id)) return

  myTracks.value = [track, ...myTracks.value]

  if (myPagination.value) {
    myPagination.value = {
      ...myPagination.value,
      total: (myPagination.value.total ?? myTracks.value.length - 1) + 1,
    }
  }
}

function reloadDifficultyFilteredLists() {
  if (!auth.isAuthenticated) {
    loadPublicTracks({ reset: true })
    return
  }

  if (isMobile.value) {
    if (activeTab.value === 'personal') {
      if (personalSubTab.value === 'my') {
        loadMyTracks({ reset: true })
      } else {
        loadFavoriteTracks({ reset: true })
      }
    } else if (activeTab.value === 'public') {
      loadPublicTracks({ reset: true })
    }
  } else {
    Promise.all([
      loadMyTracks({ reset: true }),
      loadFavoriteTracks({ reset: true }),
      loadPublicTracks({ reset: true }),
    ])
  }
}

function openTrack(id) {
  router.push({ name: 'track', params: { id } })
}

async function removeFavoriteAuthor(author) {
  removingAuthorId.value = author.id
  try {
    await unfavoriteUser(author.id)
    favoriteAuthors.value = favoriteAuthors.value.filter((item) => item.id !== author.id)
    if (favoriteAuthorsPagination.value) {
      favoriteAuthorsPagination.value = {
        ...favoriteAuthorsPagination.value,
        total: Math.max(favoriteAuthorsPagination.value.total - 1, 0),
      }
    }
  } finally {
    removingAuthorId.value = null
  }
}

watch(isMobile, () => {
  loadTracks()
})

watch(activeTab, (tab) => {
  if (!auth.isAuthenticated || !isMobile.value) return
  loadMobileTab(tab)
})

watch(personalSubTab, () => {
  if (!auth.isAuthenticated || !isMobile.value || activeTab.value !== 'personal') return
  loadPersonalMobile()
})

watch(guestActiveTab, (tab) => {
  if (auth.isAuthenticated || !isMobile.value) return
  loadGuestMobileTab(tab)
})

watch(viewMode, (mode) => {
  window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
})

watch(difficultyFilter, reloadDifficultyFilteredLists, { deep: true })

onMounted(loadTracks)
</script>
